import { Injectable } from '@angular/core';

/**
 * One row of the parsed medication detail string. The producer
 * (`MedicalHistoryRiskSummaryService.getFlaggedMedicationFactor`) emits
 * lines shaped `› ${name}\t${effects}`.
 */
export interface DrugEntry {
  name: string;
  effects: string;
}

export interface MedicationTierDrug {
  name: string;
  effectSuffix?: string;
}

export interface MedicationTierGroup {
  tier: 'irritation' | 'sight';
  tierLabel: string;
  drugs: MedicationTierDrug[];
}

/**
 * Owns the medication-card detail parsing and the clinical-risk tier
 * classification keyword set ("optic neuritis", "glaucoma", "maculopathy").
 * Lives next to the risk-summary service rather than inside the view layer
 * because the rules are clinical, not presentational.
 */
@Injectable({ providedIn: 'root' })
export class MedicalHistoryDrugParserService {
  /**
   * Producer format (medical-history-risk-summary.service): "› ${name}\t${effects}".
   * The TAB separator lets a name containing parentheses round-trip cleanly.
   */
  parseDrugDetail(detail: string): DrugEntry[] {
    return detail.split('\n')
      .map(line => line.replace(/^›\s*/, '').trim())
      .filter(line => line.length > 0)
      .map(line => {
        const tabIndex = line.indexOf('\t');
        if (tabIndex > 0) {
          return {
            name:    line.slice(0, tabIndex).trim(),
            effects: line.slice(tabIndex + 1).trim()
          };
        }
        return { name: line, effects: '' };
      });
  }

  /**
   * Splits the parsed drug list into two clinically meaningful tiers:
   * sight-threatening (any side effect mentioning optic neuritis, glaucoma,
   * or maculopathy) and dry-eye-only. The keyword check stays here next to
   * the side-effect data path rather than in the rendering component.
   */
  buildMedicationTiers(drugEntries: DrugEntry[]): MedicationTierGroup[] {
    if (!drugEntries || drugEntries.length === 0) { return []; }

    const irritationDrugs: MedicationTierDrug[] = [];
    const sightDrugs: MedicationTierDrug[] = [];

    for (const drug of drugEntries) {
      const effectsLower = (drug.effects || '').toLowerCase();
      const matchedSightEffects: string[] = [];
      if (effectsLower.includes('optic neuritis')) { matchedSightEffects.push('optic neuritis'); }
      if (effectsLower.includes('glaucoma'))       { matchedSightEffects.push('glaucoma'); }
      if (effectsLower.includes('maculopathy'))    { matchedSightEffects.push('maculopathy'); }

      if (matchedSightEffects.length > 0) {
        sightDrugs.push({ name: drug.name, effectSuffix: matchedSightEffects.join(', ') });
      } else {
        irritationDrugs.push({ name: drug.name });
      }
    }

    const groups: MedicationTierGroup[] = [];
    if (sightDrugs.length > 0) {
      groups.push({ tier: 'sight', tierLabel: 'Dry eye + sight-threatening risk', drugs: sightDrugs });
    }
    if (irritationDrugs.length > 0) {
      groups.push({ tier: 'irritation', tierLabel: 'Dry eye only', drugs: irritationDrugs });
    }
    return groups;
  }
}
